import { Link } from "@tanstack/react-router"
import {
	IconArrowsLeftRightTrailOutline18 as ArrowsLeftRight,
	IconCircleArrowUpOutline18 as ArrowUpCircle,
	IconAtom2Outline18 as Atom,
	IconArrowRotateAnticlockwiseCheckOutline18 as ArrowRotateCheck,
	IconMessageQuoteOutline18 as MessageQuote,
	IconOfficeOutline18 as Office,
	IconThumbsDownOutline18 as ThumbsDown,
	IconThumbsUpOutline18 as ThumbsUp,
	IconTicketOutline18 as Ticket,
} from "nucleo-ui-outline-18"

import type { AiResolutionChange } from "@productlane/lib"
import type { PriorityType } from "@productlane/ui"
import { AI_BUDGET_REACHED_MESSAGE } from "@productlane/lib"
import { parseThreadActivity } from "@productlane/lib/src/browser"
import {
	Avatar,
	IssueCanceledIcon,
	IssueCompletedIcon,
	LinearIcon,
	PriorityIcons,
	PriorityNaming,
	SlaBreachIcon,
	SlaIcon,
	SnoozeFilledIcon,
} from "@productlane/ui"
import { BoltIcon } from "@productlane/ui/src/icons/project/bolt-icon"
import { ClockOutlineIcon } from "@productlane/ui/src/icons/project/clockoutline-icon"
import { CrossIcon } from "@productlane/ui/src/icons/project/cross-icon"
import { CursorIcon } from "@productlane/ui/src/icons/project/cursor-icon"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"
import { IssueStatusStartedIcon } from "@productlane/ui/src/icons/project/issuestatusstarted-icon"
import { IssueStatusTodoIcon } from "@productlane/ui/src/icons/project/issuestatustodo-icon"
import { LinkIcon } from "@productlane/ui/src/icons/project/link-icon"
import { RefreshIcon } from "@productlane/ui/src/icons/project/refresh-icon"
import { TagFilledIcon } from "@productlane/ui/src/icons/project/tag-filled-icon"
import { UsersIcon } from "@productlane/ui/src/icons/project/users-icon"

import type { TimelineEventProps } from "./types"
import { getProjectIcon } from "@/lib/projects"
import { useThreadContext } from "../../../thread-context"
import { resolveActivityActorName } from "./activity-actor-name"
import { connectorRunPurpose } from "./agent-burst-summary"
import { SystemEvent } from "./base/system-event"
import { BugInspectReviewCard } from "./bug-inspect-review-card"
import { closeLoopSentLine, closeLoopSkippedLine } from "./close-loop-labels"
import { issueLabel } from "./issue-label"
import {
	linearAsksMarkedDoneLine,
	linearAsksPostedLine,
} from "./linear-asks-labels"
import { outOfOfficeAssignLine, outOfOfficeSuffix } from "./out-of-office-line"

export function ActivityEvent({
	event,
	hasEventAbove = false,
	hasEventBelow = false,
}: TimelineEventProps) {
	const { thread } = useThreadContext()

	if (event.type !== "activity") {
		return null
	}

	const { threadActivity } = event
	const actorName = resolveActivityActorName(
		threadActivity.actorName,
		threadActivity.payload,
	)

	let parsed
	try {
		parsed = parseThreadActivity(
			threadActivity.eventType,
			threadActivity.payload,
		)
	} catch {
		return null
	}

	const newAssigneeName =
		"newAssigneeName" in threadActivity
			? (threadActivity.newAssigneeName as string | null)
			: null

	const projectId =
		parsed.eventType === "project_linked" ||
		parsed.eventType === "project_unlinked" ||
		parsed.eventType === "linked_project_completed" ||
		parsed.eventType === "linked_project_cancelled"
			? parsed.payload.projectId
			: undefined
	const project = projectId
		? thread.projects.find((p) => p.id === projectId)
		: undefined

	const issueId =
		"issueId" in parsed.payload ? parsed.payload.issueId : undefined
	const issue = issueId
		? thread.issues.find((i) => i.id === issueId)
		: undefined

	const { icon, text, hover } = getEventDisplay(
		parsed,
		actorName,
		threadActivity.createdAt,
		newAssigneeName,
		project,
		issue,
	)

	const row = (
		<SystemEvent
			icon={icon}
			timestamp={new Date(threadActivity.createdAt)}
			hasEventAbove={hasEventAbove}
			hasEventBelow={hasEventBelow}
		>
			<span className="truncate text-tertiary" title={hover}>
				{text}
			</span>
		</SystemEvent>
	)

	if (parsed.eventType === "bug_inspect_pr_opened") {
		return (
			<div>
				{row}
				<BugInspectReviewCard runId={parsed.payload.runId} />
			</div>
		)
	}

	return row
}

function formatDuration(ms: number): string {
	const minutes = Math.round(ms / 60000)
	if (minutes < 60) return `${minutes} min`
	const hours = Math.round(minutes / 60)
	if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""}`
	const days = Math.round(hours / 24)
	return `${days} day${days !== 1 ? "s" : ""}`
}

function PrLink({
	url,
	number,
	title,
	verb,
	issueIdentifier,
}: {
	url: string
	number?: number
	title: string
	verb: "opened" | "merged" | "closed"
	issueIdentifier?: string
}) {
	return (
		<>
			{issueIdentifier ? (
				<span className="font-medium text-primary">{issueIdentifier}</span>
			) : null}
			{issueIdentifier ? " " : ""}
			<a
				href={url}
				target="_blank"
				rel="noopener noreferrer"
				className="truncate hover:underline"
			>
				{`PR #${number ?? ""} ${verb}: ${title}`.trim()}
			</a>
		</>
	)
}

function LinearLink({
	url,
	label,
	before,
	after,
}: {
	url?: string | null
	label: string
	before?: string
	after?: string
}) {
	return (
		<>
			{before ? `${before} ` : ""}
			{url ? (
				<a
					href={url}
					target="_blank"
					rel="noopener noreferrer"
					className="text-secondary hover:underline"
				>
					{label}
				</a>
			) : (
				label
			)}
			{after ? ` ${after}` : ""}
		</>
	)
}

function getCodingAgentIcon(provider: string | undefined, className: string) {
	if (provider === "cursor") {
		return <CursorIcon className={className} />
	}
	return <Atom className={className} />
}

function skippedReplyLabel(
	ground: "automated" | "bulk" | "no-help" | "silent",
	detail: string | undefined,
): string {
	const reason =
		ground === "automated"
			? "the message came from a machine"
			: ground === "bulk"
				? "the message is bulk mail"
				: ground === "no-help"
					? "it could not help with this one"
					: "it had nothing to answer"
	return `Productlane Agent sent no reply because ${reason}${detail ? `: ${detail}` : ""}`
}

function reopenedLabel(
	source:
		| "ai_agent"
		| "ai_handoff"
		| "ai_notified"
		| "customer_reply"
		| "email_bounce"
		| "unanswered_message"
		| "linear_asks"
		| undefined,
	actorName: string,
): string {
	switch (source) {
		case "customer_reply":
			return "Reopened by a customer reply"
		case "ai_handoff":
			return "Reopened for a handoff to a person"
		case "ai_notified":
			return "Reopened so the team sees the report"
		case "email_bounce":
			return "Reopened because the reply bounced"
		case "unanswered_message":
			return "Reopened for an unanswered message"
		default:
			return `${actorName} reopened`
	}
}

const AI_RESOLUTION_LINES: Record<AiResolutionChange, string> = {
	judged_resolved: "Counted as resolved by the agent after review",
	judged_escalated: "Counted as escalated after review: a teammate was needed",
	assumed_resolved:
		"Counted as resolved: the customer stayed quiet for 24 hours",
	revoked: "Customer came back, so it no longer counts as resolved",
}

export function getEventDisplay(
	parsed: ReturnType<typeof parseThreadActivity>,
	actorName: string,
	createdAt: number | Date,
	newAssigneeName?: string | null,
	project?: {
		icon?: string | null
		color?: string | null
		url?: string | null
		name?: string | null
	},
	issue?: {
		url?: string | null
		identifier?: string | null
		title?: string | null
	},
): { icon: React.ReactNode; text: React.ReactNode; hover?: string } {
	const iconClass = "h-3 min-h-3 w-3 min-w-3"

	switch (parsed.eventType) {
		case "thread_snoozed": {
			const duration = parsed.payload.snoozedUntil
				? formatDuration(
						new Date(parsed.payload.snoozedUntil).getTime() -
							new Date(createdAt).getTime(),
					)
				: ""
			return {
				icon: <SnoozeFilledIcon className={`${iconClass} text-secondary`} />,
				text: `${actorName} snoozed${duration ? ` for ${duration}` : ""}`,
			}
		}
		case "thread_unsnoozed":
			return {
				icon: <SnoozeFilledIcon className={`${iconClass} text-secondary`} />,
				text: `${actorName} unsnoozed`,
			}
		case "thread_marked_done": {
			if (parsed.payload.source === "linear_asks") {
				const line = linearAsksMarkedDoneLine(parsed.payload)
				return {
					icon:
						parsed.payload.outcome === "canceled" ? (
							<IssueCanceledIcon className={`${iconClass} text-secondary`} />
						) : (
							<IssueCompletedIcon className={`${iconClass} text-secondary`} />
						),
					text: line,
					hover: line,
				}
			}
			return {
				icon: <IssueCompletedIcon className={`${iconClass} text-secondary`} />,
				text:
					parsed.payload.source !== "ai_agent"
						? `${actorName} marked as done`
						: parsed.payload.reason === "inactivity"
							? "AI agent marked as done after the customer went quiet"
							: "AI agent marked as done",
			}
		}
		case "thread_reopened":
			return {
				icon: <RefreshIcon className={`${iconClass} text-secondary`} />,
				text: reopenedLabel(parsed.payload.source, actorName),
			}
		case "thread_merged": {
			const sourceLabel =
				parsed.payload.sourceTitle?.trim() || "Untitled thread"
			const targetLabel =
				parsed.payload.targetTitle?.trim() || "Untitled thread"
			return {
				icon: <ArrowsLeftRight className={`${iconClass} text-secondary`} />,
				text: (
					<span className="inline">
						{actorName} merged{" "}
						<Link
							to="/threads/$id"
							params={{ id: parsed.payload.sourceThreadId }}
							className="truncate hover:underline"
						>
							{sourceLabel}
						</Link>{" "}
						into{" "}
						<Link
							to="/threads/$id"
							params={{ id: parsed.payload.targetThreadId }}
							className="truncate hover:underline"
						>
							{targetLabel}
						</Link>
					</span>
				),
			}
		}
		case "thread_split": {
			const count = parsed.payload.messageCount
			const sourceLabel =
				parsed.payload.sourceTitle?.trim() || "Untitled thread"
			return {
				icon: <ArrowsLeftRight className={`${iconClass} text-secondary`} />,
				text: (
					<span className="inline">
						{actorName} moved {count} message{count === 1 ? "" : "s"} from{" "}
						<Link
							to="/threads/$id"
							params={{ id: parsed.payload.sourceThreadId }}
							className="truncate hover:underline"
						>
							{sourceLabel}
						</Link>{" "}
						to{" "}
						<Link
							to="/threads/$id"
							params={{ id: parsed.payload.newThreadId }}
							className="truncate hover:underline"
						>
							{parsed.payload.newTitle}
						</Link>
					</span>
				),
			}
		}
		case "assignee_changed":
			return {
				icon: <UsersIcon className={`${iconClass} text-secondary`} />,
				text: parsed.payload.outOfOffice
					? outOfOfficeAssignLine({
							handoff: parsed.payload.outOfOffice,
							actorName,
							newAssigneeName: newAssigneeName ?? null,
							moved: !!parsed.payload.oldAssigneeId,
						})
					: newAssigneeName
						? `${actorName} assigned ${newAssigneeName}`
						: `${actorName} unassigned`,
			}
		case "self_assigned":
			return {
				icon: <UsersIcon className={`${iconClass} text-secondary`} />,
				text: `${actorName} self-assigned`,
			}
		case "contact_changed": {
			const oldName = parsed.payload.oldContactName
			const newName = parsed.payload.newContactName
			return {
				icon: newName ? (
					<Avatar
						name={newName}
						image={parsed.payload.newContactImage ?? null}
						size="xxs"
						roundness="full"
					/>
				) : (
					<UsersIcon className={`${iconClass} text-secondary`} />
				),
				text: !newName
					? `${actorName} removed contact`
					: oldName
						? `${actorName} changed contact from ${oldName} to ${newName}`
						: `${actorName} set contact to ${newName}`,
			}
		}
		case "company_changed": {
			const oldName = parsed.payload.oldCompanyName
			const newName = parsed.payload.newCompanyName
			return {
				icon: newName ? (
					<Avatar
						name={newName}
						image={parsed.payload.newCompanyImage ?? null}
						size="xxs"
						roundness="md"
					/>
				) : (
					<Office className={`${iconClass} text-secondary`} />
				),
				text: !newName
					? `${actorName} removed company`
					: oldName
						? `${actorName} changed company from ${oldName} to ${newName}`
						: `${actorName} set company to ${newName}`,
			}
		}
		case "issue_linked":
			if (parsed.payload.linkedVia === "linear_asks") {
				return {
					icon: <Ticket className={`${iconClass} text-secondary`} />,
					text: (
						<LinearLink
							url={issue?.url}
							before="Linear Asks created"
							label={
								parsed.payload.issueIdentifier ??
								issueLabel(parsed.payload, issue)
							}
							after="from this message and Productlane linked it"
						/>
					),
				}
			}
			return {
				icon: <LinearIcon className={`${iconClass} text-secondary`} />,
				text: (
					<LinearLink
						url={issue?.url}
						before={`${actorName} linked`}
						label={issueLabel(parsed.payload, issue)}
					/>
				),
			}
		case "issue_unlinked":
			return {
				icon: <LinkIcon className={`${iconClass} text-secondary`} />,
				text: (
					<LinearLink
						url={issue?.url}
						before={`${actorName} unlinked`}
						label={issueLabel(parsed.payload, issue)}
					/>
				),
			}
		case "issue_created":
			return {
				icon: <LinearIcon className={`${iconClass} text-secondary`} />,
				text: (
					<LinearLink
						url={issue?.url}
						label={issueLabel(parsed.payload, issue)}
						after="created"
					/>
				),
			}
		case "project_linked": {
			const ProjectIcon = getProjectIcon(project?.icon)
			return {
				icon: <ProjectIcon className={`${iconClass} text-secondary`} />,
				text: (
					<LinearLink
						url={project?.url}
						before={`${actorName} linked project`}
						label={parsed.payload.projectName ?? project?.name ?? "project"}
					/>
				),
			}
		}
		case "project_unlinked": {
			const ProjectIcon = getProjectIcon(project?.icon)
			return {
				icon: <ProjectIcon className={`${iconClass} text-secondary`} />,
				text: (
					<LinearLink
						url={project?.url}
						before={`${actorName} unlinked project`}
						label={parsed.payload.projectName ?? project?.name ?? "project"}
					/>
				),
			}
		}
		case "tag_linked": {
			const TagLinkedIcon = parsed.payload.tagIcon
				? getProjectIcon(parsed.payload.tagIcon)
				: TagFilledIcon
			return {
				icon: <TagLinkedIcon className={`${iconClass} text-secondary`} />,
				text: `${actorName} added tag ${parsed.payload.tagName ?? ""}`.trim(),
			}
		}
		case "tag_unlinked": {
			const TagUnlinkedIcon = parsed.payload.tagIcon
				? getProjectIcon(parsed.payload.tagIcon)
				: TagFilledIcon
			return {
				icon: <TagUnlinkedIcon className={`${iconClass} text-secondary`} />,
				text: `${actorName} removed tag ${parsed.payload.tagName ?? ""}`.trim(),
			}
		}
		case "priority_changed": {
			const PriorityIcon =
				PriorityIcons[parsed.payload.newPriority as PriorityType] ??
				ArrowUpCircle
			const oldName =
				PriorityNaming[parsed.payload.oldPriority as PriorityType] ??
				parsed.payload.oldPriority
			const newName =
				PriorityNaming[parsed.payload.newPriority as PriorityType] ??
				parsed.payload.newPriority
			return {
				icon: <PriorityIcon className={`${iconClass} text-secondary`} />,
				text: `${actorName} changed importance from ${oldName} to ${newName}`,
			}
		}
		case "status_changed":
			return {
				icon: <BoltIcon className={`${iconClass} text-secondary`} />,
				text: `${actorName} changed status from ${parsed.payload.oldStatus} to ${parsed.payload.newStatus}`,
			}
		case "linked_issue_planned":
			return {
				icon: <IssueStatusTodoIcon className={`${iconClass} text-secondary`} />,
				text: (
					<LinearLink
						url={issue?.url}
						before="Linked issue"
						label={issueLabel(parsed.payload, issue)}
						after="moved to planned"
					/>
				),
			}
		case "linked_issue_started":
			return {
				icon: (
					<IssueStatusStartedIcon className={`${iconClass} text-secondary`} />
				),
				text: (
					<LinearLink
						url={issue?.url}
						before="Linked issue"
						label={issueLabel(parsed.payload, issue)}
						after="moved to in progress"
					/>
				),
			}
		case "linked_issue_completed":
			return {
				icon: <IssueCompletedIcon className={`${iconClass} text-secondary`} />,
				text: (
					<LinearLink
						url={issue?.url}
						before="Linked issue"
						label={issueLabel(parsed.payload, issue)}
						after="completed"
					/>
				),
			}
		case "linked_issue_cancelled":
			return {
				icon: <IssueCanceledIcon className={`${iconClass} text-secondary`} />,
				text: (
					<LinearLink
						url={issue?.url}
						before="Linked issue"
						label={issueLabel(parsed.payload, issue)}
						after="cancelled"
					/>
				),
			}
		case "linked_project_completed": {
			const ProjectIcon = getProjectIcon(project?.icon)
			return {
				icon: <ProjectIcon className={`${iconClass} text-secondary`} />,
				text: (
					<LinearLink
						url={project?.url}
						before="Linked project"
						label={parsed.payload.projectName ?? project?.name ?? "project"}
						after="completed"
					/>
				),
			}
		}
		case "linked_project_cancelled": {
			return {
				icon: <CrossIcon className={`${iconClass} text-secondary`} />,
				text: (
					<LinearLink
						url={project?.url}
						before="Linked project"
						label={parsed.payload.projectName ?? project?.name ?? "project"}
						after="cancelled"
					/>
				),
			}
		}
		case "sla_started":
			return {
				icon: <SlaIcon className={`${iconClass} text-secondary`} />,
				text: `SLA started${parsed.payload.policyName ? ` (${parsed.payload.policyName})` : ""}`,
			}
		case "sla_paused":
			return {
				icon: <ClockOutlineIcon className={`${iconClass} text-secondary`} />,
				text: "SLA paused",
			}
		case "sla_breached":
			return {
				icon: <SlaBreachIcon className={`${iconClass} text-secondary`} />,
				text: "SLA breached",
			}
		case "sla_resolved":
			return {
				icon: <SlaIcon className={`${iconClass} text-secondary`} />,
				text: "SLA resolved",
			}
		case "sla_policy_set": {
			const mins = parsed.payload.durationMinutes
			const duration =
				mins >= 1440
					? `${Math.round(mins / 1440)}d`
					: mins >= 60
						? `${Math.round(mins / 60)}h`
						: `${mins}m`
			return {
				icon: <SlaIcon className={`${iconClass} text-secondary`} />,
				text: `${actorName} set SLA to ${duration}`,
			}
		}
		case "sla_policy_removed":
			return {
				icon: <SlaIcon className={`${iconClass} text-secondary`} />,
				text: `${actorName} removed SLA`,
			}
		case "auto_assigned": {
			const reasonText: Record<string, string> = {
				linear_customer_owner: "Linear customer owner",
				close_loop_owner: "owner of the released work",
			}
			return {
				icon: <UsersIcon className={`${iconClass} text-secondary`} />,
				text: `Auto-assigned (${reasonText[parsed.payload.reason] ?? parsed.payload.reason})${
					parsed.payload.outOfOffice
						? `. ${outOfOfficeSuffix(parsed.payload.outOfOffice)}`
						: ""
				}`,
			}
		}
		case "pr_opened":
			return {
				icon: <GitHubIcon className={`${iconClass} text-secondary`} />,
				text: (
					<PrLink
						url={parsed.payload.url}
						number={parsed.payload.number}
						title={parsed.payload.title}
						verb="opened"
						issueIdentifier={parsed.payload.issueIdentifier}
					/>
				),
			}
		case "pr_merged":
			return {
				icon: <GitHubIcon className={`${iconClass} text-secondary`} />,
				text: (
					<PrLink
						url={parsed.payload.url}
						number={parsed.payload.number}
						title={parsed.payload.title}
						verb="merged"
						issueIdentifier={parsed.payload.issueIdentifier}
					/>
				),
			}
		case "pr_closed":
			return {
				icon: <GitHubIcon className={`${iconClass} text-secondary`} />,
				text: (
					<PrLink
						url={parsed.payload.url}
						number={parsed.payload.number}
						title={parsed.payload.title}
						verb="closed"
						issueIdentifier={parsed.payload.issueIdentifier}
					/>
				),
			}
		case "coding_agent_triggered": {
			const issueRef = parsed.payload.issueIdentifier ?? "issue"
			const isAuto =
				parsed.payload.triggerSource === "auto-resolve" ||
				parsed.payload.triggerSource === "ai-chat"
			const text = isAuto
				? `Coding agent auto-assigned to ${issueRef}`
				: `${actorName} triggered coding agent for ${issueRef}`
			return {
				icon: getCodingAgentIcon(
					parsed.payload.provider,
					`${iconClass} text-secondary`,
				),
				text,
			}
		}
		case "coding_agent_running": {
			const issueRef = parsed.payload.issueIdentifier ?? "issue"
			return {
				icon: getCodingAgentIcon(
					parsed.payload.provider,
					`${iconClass} text-secondary`,
				),
				text: `Coding agent started on ${issueRef}`,
			}
		}
		case "coding_agent_finished": {
			const issueRef = parsed.payload.issueIdentifier ?? "issue"
			const prSuffix = parsed.payload.prUrl ? " with PR" : ""
			return {
				icon: getCodingAgentIcon(
					parsed.payload.provider,
					`${iconClass} text-secondary`,
				),
				text: `Coding agent finished ${issueRef}${prSuffix}`,
			}
		}
		case "coding_agent_failed": {
			const issueRef = parsed.payload.issueIdentifier ?? "issue"
			return {
				icon: getCodingAgentIcon(
					parsed.payload.provider,
					`${iconClass} text-secondary`,
				),
				text: `Coding agent failed on ${issueRef}`,
			}
		}
		case "coding_agent_stopped": {
			const issueRef = parsed.payload.issueIdentifier ?? "issue"
			return {
				icon: getCodingAgentIcon(
					parsed.payload.provider,
					`${iconClass} text-secondary`,
				),
				text: `${actorName} stopped coding agent on ${issueRef}`,
			}
		}
		case "bug_inspect_requested": {
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: "Bug Inspect started checking this report against the code",
			}
		}
		case "bug_inspect_reproduced": {
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: "Bug Inspect reproduced the reported bug",
			}
		}
		case "bug_inspect_not_reproduced": {
			const verdictText =
				parsed.payload.verdict === "NEEDS_MORE_INFO"
					? "needs more detail from the customer"
					: parsed.payload.verdict === "INCONCLUSIVE"
						? "could not reach the code it needed"
						: "did not find the reported bug in the code"
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: `Bug Inspect ${verdictText}`,
			}
		}
		case "bug_inspect_blocked": {
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: `Bug Inspect did not run (${parsed.payload.reason})`,
			}
		}
		case "bug_inspect_pr_opened": {
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: `Bug Inspect opened draft pull request #${parsed.payload.prNumber}`,
			}
		}
		case "bug_inspect_approved": {
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: `Approved the fix and marked pull request #${parsed.payload.prNumber} ready for review`,
			}
		}
		case "bug_inspect_rejected": {
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: `Rejected the fix, closed pull request #${parsed.payload.prNumber} and deleted its branch`,
			}
		}
		case "docs_proposal_started": {
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: "Started creating documentation update proposal",
			}
		}
		case "docs_proposal_ready": {
			const count = parsed.payload.childCount
			const changeWord = count === 1 ? "change" : "changes"
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: (
					<Link
						to={parsed.payload.reviewPath ?? "/edit-docs"}
						className="truncate hover:underline"
					>
						Documentation proposal is ready ({count} {changeWord})
					</Link>
				),
			}
		}
		case "docs_proposal_skipped": {
			// A paused budget means the agent never read the docs, so claiming they
			// already cover the thread would be a guess dressed as a finding.
			if (parsed.payload.reason === AI_BUDGET_REACHED_MESSAGE) {
				return {
					icon: <Atom className={`${iconClass} text-secondary`} />,
					text: (
						<span className="flex min-w-0 flex-wrap items-center gap-1.5">
							<span>Docs proposal skipped: the AI spend limit is reached.</span>
							<Link
								to="/settings/ai/usage"
								className="font-medium text-secondary hover:text-primary"
							>
								Raise the limit
							</Link>
						</span>
					),
				}
			}
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: "Documentation already covers this",
			}
		}
		case "custom_data_action_run": {
			const { action, sectionTitle, actor, source, success, message } =
				parsed.payload
			const subject = actor === "agent" ? "Productlane Agent" : actorName
			const verb = success ? "ran" : "could not run"
			const line = `${subject} ${verb} "${action}" in ${sectionTitle}${connectorRunPurpose(
				source,
			)}${message ? `: ${message}` : ""}`
			return {
				icon: <BoltIcon className={`${iconClass} text-secondary`} />,
				text: <span className="truncate">{line}</span>,
				// The row truncates, and a failure reason is the whole reason this
				// event exists, so the full text has to be readable somewhere.
				hover: line,
			}
		}
		case "ai_auto_reply_started": {
			return {
				icon: <Atom className={`${iconClass} animate-pulse text-secondary`} />,
				text: (
					<span className="animate-pulse truncate">
						Agent is writing a reply
					</span>
				),
			}
		}
		// Rendered on the auto-reply itself, so it never reaches the timeline.
		case "ai_auto_reply_connectors":
			return {
				icon: <BoltIcon className={`${iconClass} text-secondary`} />,
				text: <span className="truncate">Auto-reply used connectors</span>,
			}
		// Read by Improve reply on the message itself, so it never reaches the timeline.
		case "ai_reply_trace":
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: <span className="truncate">Agent looked things up</span>,
			}
		case "ai_handoff": {
			const byAgent = parsed.payload.reason === "agent_decision"
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: byAgent
					? "Productlane Agent handed this conversation to the team"
					: "Customer asked to talk with a human",
			}
		}
		case "ai_notified":
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: "Productlane Agent notified the team",
			}
		case "ai_handoff_reviewed":
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: `${actorName} reviewed the handoff`,
			}
		case "ai_reply_skipped": {
			const line = skippedReplyLabel(
				parsed.payload.ground,
				parsed.payload.detail,
			)
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: <span className="truncate">{line}</span>,
				hover: line,
			}
		}
		case "ai_draft_skipped": {
			const line = `No draft written: ${parsed.payload.summary}`
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: <span className="truncate">{line}</span>,
				hover: line,
			}
		}
		case "routine_run": {
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: "Routine ran on this thread",
			}
		}
		case "routine_note": {
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text:
					parsed.payload.kind === "translation"
						? "Routine translated the message"
						: "Routine left a note",
			}
		}
		case "ai_shadow_reply": {
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: "Productlane Agent wrote a preview reply",
			}
		}
		case "close_loop_sent": {
			const line = closeLoopSentLine(parsed.payload)
			return {
				icon: <ArrowRotateCheck className={`${iconClass} text-secondary`} />,
				text: <span className="truncate">{line}</span>,
				hover: line,
			}
		}
		case "close_loop_skipped": {
			const line = closeLoopSkippedLine(parsed.payload)
			return {
				icon: <ArrowRotateCheck className={`${iconClass} text-secondary`} />,
				text: <span className="truncate">{line}</span>,
				hover: parsed.payload.detail
					? `${line}. ${parsed.payload.detail}`
					: line,
			}
		}
		case "linear_asks_posted": {
			const line = linearAsksPostedLine(parsed.payload)
			return {
				icon:
					parsed.payload.kind === "comment" ? (
						<MessageQuote className={`${iconClass} text-secondary`} />
					) : (
						<Ticket className={`${iconClass} text-secondary`} />
					),
				text: <span className="truncate">{line}</span>,
				hover: line,
			}
		}
		case "ai_resolution_changed": {
			const line = AI_RESOLUTION_LINES[parsed.payload.change]
			return {
				icon: <Atom className={`${iconClass} text-secondary`} />,
				text: <span className="truncate">{line}</span>,
				hover: parsed.payload.reason ?? line,
			}
		}
		case "ai_chat_feedback": {
			const isPositive = parsed.payload.sentiment === "positive"
			const note = parsed.payload.message?.trim()
			const verb = isPositive ? "liked" : "disliked"
			return {
				icon: isPositive ? (
					<ThumbsUp className={`${iconClass} text-secondary`} />
				) : (
					<ThumbsDown className={`${iconClass} text-secondary`} />
				),
				text: `Customer ${verb} the agent's reply${note ? `: ${note}` : ""}`,
			}
		}
	}

	// A thread is readable even when it carries an event this build has no case
	// for, which is what a deploy mid-rollout looks like from the dashboard.
	return {
		icon: <Atom className={`${iconClass} text-secondary`} />,
		text: String(
			(parsed as { eventType?: string }).eventType ?? "activity",
		).replace(/_/g, " "),
	}
}
